/* MISHKA cartdate (Oct 21): one delivery date per cart.
   Identixweb stores each line's date as cart attribute product_<variant_id> = "Title |&| dd/mm/yyyy |&| Weekday".
   After any cart add/change/update (fetch or XHR) and on every page load, copy the cart's existing date to any line
   that has none, so /cart never shows "The selected date and time is either in the past or unavailable".
   On product pages, prefill the picker with the cart's date so the customer never picks twice.
   Oct 26 datecheck: (a) a cart date is validated (Identixweb blocked dates, weekdays, earliest/cutoff, 30-day window)
   before prefill, sync and Checkout; an invalid date blocks Checkout with an inline message and a "Remove date" option;
   (b) .post-date-error is only removed when the cart has no invalid date; (c) the date is no longer copied onto
   lines that have no date of their own (no carry-over). An undated cart always checks out. */
(() => {
  if (window.MkCartDate) return;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const SEP = ' |&| ';
  const nativeFetch = window.fetch.bind(window);
  const CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  let busy = false, timer;

  /* ---- Oct 26 datecheck: per-product rules from the Identixweb addons response (the app's own XHR on product
     pages), cached in localStorage. HARD-CODED FALLBACK GUARD: 10/31/2026 (Halloween) is ALWAYS blocked, and when no
     app data is cached the defaults below (Wed-Sat, 2 PM PT cutoff, 2-day lead, 30-day window) are used. ---- */
  const RKEY = 'mkOddRules', ALWAYS_BLOCKED = ['10/31/2026'];
  const FALLBACK = { days: [3, 4, 5, 6], minInt: 2, maxInt: 30, cutoff: 14, blocked: [] };
  let rules = {}; try { rules = JSON.parse(localStorage.getItem(RKEY) || '{}') || {}; } catch (e) {}
  function learn(txt) {
    try {
      const d = JSON.parse(txt).data; if (!d || typeof d !== 'object') return;
      let n = 0;
      Object.keys(d).forEach((pid) => {
        const c = d[pid]; if (!/^\d+$/.test(pid) || !c || !Array.isArray(c.disable_date)) return;
        const co = c.cutoff_final && +c.cutoff_final.cutoff_time_status ? parseInt(c.cutoff_final.cutoff_24, 10) : null;
        rules[pid] = { days: (c.available_days || []).map(Number), blocked: Array.from(new Set(c.disable_date)),
          minInt: +c.min_interval_time || 0, maxInt: +c.max_interval_time || 0, cutoff: isNaN(co) ? null : co, at: Date.now() };
        n++;
      });
      if (n) { try { localStorage.setItem(RKEY, JSON.stringify(rules)); } catch (e) {} check(); }
    } catch (e) {}
  }
  function ptNow() { // {y,m,d,h} in America/Los_Angeles
    const f = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });
    const o = {}; f.formatToParts(new Date()).forEach((p) => { o[p.type] = +p.value; }); return o;
  }
  /* Returns '' when the dd/mm/yyyy date is OK for the product, otherwise a short reason (for debugging). */
  function invalidReason(ddmmyyyy, productId) {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(ddmmyyyy || '').trim()); if (!m) return 'format';
    const day = +m[1], mon = +m[2], yr = +m[3];
    const us = String(mon).padStart(2, '0') + '/' + String(day).padStart(2, '0') + '/' + yr;
    const r = rules[productId] || FALLBACK;
    if (ALWAYS_BLOCKED.includes(us) || (r.blocked || []).includes(us)) return 'blocked';
    const dt = Date.UTC(yr, mon - 1, day), wd = new Date(dt).getUTCDay();
    if (new Date(dt).getUTCDate() !== day) return 'format';
    if (r.days && r.days.length && !r.days.includes(wd)) return 'weekday';
    const n = ptNow(), today = Date.UTC(n.year, n.month - 1, n.day), D = 864e5;
    const lead = (r.minInt || 0) + (r.cutoff != null && n.hour >= r.cutoff ? 1 : 0);
    if (dt < today + lead * D) return 'too-early';
    if (r.maxInt && dt > today + (r.maxInt + lead) * D) return 'too-late';
    return '';
  }
  /* Every dated line in the cart, checked against its own product's rules. */
  function badLines(cart) {
    const a = (cart && cart.attributes) || {}, out = [];
    ((cart && cart.items) || []).forEach((i) => {
      const k = 'product_' + i.variant_id, p = parts(a[k]);
      if (p && p[1].trim() && !out.some((o) => o.key === k)) {
        const why = invalidReason(p[1], String(i.product_id)); if (why) out.push({ key: k, item: i, date: p[1].trim(), why });
      }
    });
    return out;
  }

  async function getCart() {
    return (await nativeFetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();
  }
  /* Oct 22 r8: ONE order-level date = the most recently shopper-set date.
     Meta cart attributes (underscore = hidden at checkout): _mk_date = "dd/mm/yyyy |&| Weekday" (order date),
     _mk_date_src = attribute key of the line whose pick set it, _mk_date_copies = keys this script wrote (copies).
     A line whose value differs from _mk_date (or any dated line not in _mk_date_copies) was set by the shopper.
     Ties / legacy carts (no meta yet): newest line wins (cart.items is newest-first). */
  const parts = (v) => { const p = String(v || '').split(SEP); return p[1] ? p : null; };
  function resolve(cart) {
    const a = (cart && cart.attributes) || {}, items = (cart && cart.items) || [];
    const keys = []; items.forEach((i) => { const k = 'product_' + i.variant_id; if (!keys.includes(k)) keys.push(k); });
    const ord = a._mk_date ? String(a._mk_date).split(SEP) : null; // [date, weekday]
    let copies = String(a._mk_date_copies || '').split(',').filter((k) => k && keys.includes(k));
    const fresh = keys.filter((k) => { const p = parts(a[k]); return p && (!ord || p[1] !== ord[0]); });
    let src = a._mk_date_src || '', date = ord;
    if (fresh.length) { src = fresh[0]; const p = parts(a[src]); date = [p[1], p[2] || '']; copies = copies.filter((k) => k !== src); }
    else if (ord && !keys.includes(src)) {
      const orig = keys.find((k) => parts(a[k]) && !copies.includes(k));
      if (orig) src = orig; else { src = ''; date = null; }
    } else if (!ord) { date = null; src = ''; }
    const item = src && items.find((i) => 'product_' + i.variant_id === src);
    return { date, src, item, keys, copies, attrs: a };
  }
  function cartDate(cart) { // legacy shape used by prefill: [title, dd/mm/yyyy, weekday]
    const r = resolve(cart); return r.date ? ['', r.date[0], r.date[1]] : null;
  }
  /* MISHKA welcomefix (Oct 21): the Klaviyo welcome code (free FedEx 2Day, $65+ first order) is pointless at $149+
     where shipping is already free, and checkout then shows "WELCOME-XXXX discount code isn't available for your
     shipping address". At $149+ remove it from the cart and remember it; below $149 put the remembered code back. */
  const FREE_AT = 14900, WKEY = 'mkWelcomeCode';
  const isWelcome = (c) => /^WELCOME-/i.test(c || '') || /^MISHKA-FS-SEED-INTERNAL$/i.test(c || '');
  let wBusy = false;
  function hideWelcome(code) {
    document.documentElement.classList.toggle('mk-welcome-off', !!code);
    if (!code) return;
    document.querySelectorAll('cart-drawer li, cart-drawer .discounts *, #main-cart li, #main-cart .discounts *, .cart__footer li, .totals li, .cart-drawer__footer li')
      .forEach((el) => { if (el.children.length < 4 && el.textContent.toUpperCase().includes(code.toUpperCase())) el.style.display = 'none'; });
  }
  function clearWelcomeCookie() {
    try {
      const m = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
      const ck = m ? decodeURIComponent(m[1]) : '';
      if (ck && isWelcome(ck)) {
        try { localStorage.setItem(WKEY, ck); } catch (e) {}
        document.cookie = 'discount_code=; Max-Age=0; path=/';
        document.cookie = 'discount_code=; Max-Age=0; path=/; domain=' + location.hostname.replace(/^www\./, '');
      }
    } catch (e) {}
  }
  async function welcome(cart, opts) {
    if (wBusy && !(opts && opts.force)) return;
    wBusy = true;
    try {
      cart = cart || await getCart();
      const codes = (cart.discount_codes || []).map((d) => d.code);
      const applied = codes.find(isWelcome);
      if (applied) { try { localStorage.setItem(WKEY, applied); } catch (e) {} }
      const sub = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
      let body = null;
      if (sub >= FREE_AT && cart.item_count > 0) clearWelcomeCookie();
      if (applied && sub >= FREE_AT) body = { discount: codes.filter((c) => !isWelcome(c)).join(',') };
      else if (!applied && sub < FREE_AT && cart.item_count > 0) {
        let saved = null; try { saved = localStorage.getItem(WKEY); } catch (e) {}
        if (saved) body = { discount: codes.concat(saved).join(',') };
      }
      if (body) {
        await nativeFetch(root + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Mk-Cart-Date': '1' },
          body: JSON.stringify(body)
        });
        if (!(opts && opts.noReload) && document.getElementById('main-cart') && !sessionStorage.getItem('mkWelcomeReload')) {
          sessionStorage.setItem('mkWelcomeReload', '1'); location.reload(); return;
        }
      } else { sessionStorage.removeItem('mkWelcomeReload'); }
      hideWelcome(applied && sub >= FREE_AT ? applied : null);
    } catch (e) {} finally { wBusy = false; }
  }

  async function sync(opts) {
    if (busy) return false;
    busy = true;
    try {
      let cart = await getCart();
      if (!cart.items.length) return false;
      /* Oct 29 rekey (#3849): a date picked before a variant switch stays keyed to the old variant
         (product_<Lamb> while the cart holds Turkey) and is then dropped. Re-key it to an undated cart line
         of the same product, never drop it. Additive only; the date stays optional. */
      try {
        const at = cart.attributes || {}, inCart = cart.items.map((i) => 'product_' + i.variant_id), mv = {};
        Object.keys(at).forEach((k) => {
          if (!/^product_\d+$/.test(k) || inCart.includes(k) || !parts(at[k])) return;
          const title = String(at[k]).split(SEP)[0].trim();
          const it = cart.items.find((i) => { const kk = 'product_' + i.variant_id; return !parts(at[kk]) && !mv[kk] && title.indexOf(i.product_title) === 0; });
          if (!it) return;
          const p = parts(at[k]), kk = 'product_' + it.variant_id;
          mv[kk] = it.product_title + ' - ' + (it.variant_title || '') + SEP + p[1] + SEP + (p[2] || '');
          if (!at._mk_date || at._mk_date_src === k) { mv._mk_date = p[1] + SEP + (p[2] || ''); mv._mk_date_src = kk; }
        });
        if (Object.keys(mv).length) {
          if (mv.calendar_type == null && at.calendar_type !== 'product') mv.calendar_type = 'product';
          await nativeFetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Mk-Cart-Date': '1' }, body: JSON.stringify({ attributes: mv }) });
          cart = await getCart();
        }
      } catch (e) {}
      const r = resolve(cart), a = r.attrs, set = {};
      let copies = r.copies.slice();
      if (r.date) {
        const val = r.date.join(SEP);
        /* Oct 26: no carry-over. A line without its own date is left undated (the date is optional), and a date
           is never copied onto another line. Copies written by the old version are cleared, so an undated line
           added later never inherits the date. */
        r.keys.forEach((k) => { if (k !== r.src && a[k] && r.copies.includes(k)) set[k] = ''; });
        copies = [];
        if (r.item && invalidReason(r.date[0], String(r.item.product_id))) { /* invalid: leave it for the Checkout guard, don't spread it */ }
        else {
          if (a._mk_date !== val) set._mk_date = val;
          if (a._mk_date_src !== r.src) set._mk_date_src = r.src;
        }
        if (a.calendar_type !== 'product') set.calendar_type = 'product';
        if (!a['Date Format']) set['Date Format'] = 'dd/mm/yy';
      } else if (a._mk_date) {
        /* The shopper-set line was removed and no remaining line had its own pick: drop the copied dates. */
        r.keys.forEach((k) => { if (a[k] && r.copies.includes(k)) set[k] = ''; });
        set._mk_date = ''; set._mk_date_src = ''; copies = [];
      }
      const cs = copies.join(',');
      if ((a._mk_date_copies || '') !== cs) set._mk_date_copies = cs;
      if (!Object.keys(set).length) return false;
      await nativeFetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Mk-Cart-Date': '1' },
        body: JSON.stringify({ attributes: set })
      });
      document.dispatchEvent(new CustomEvent('mk:cartdate'));
      // The cart page renders the per-line date boxes from the attributes at load; refresh once so they match.
      if (opts && opts.reloadCartPage && document.getElementById('main-cart')) {
        if (!sessionStorage.getItem('mkCartDateReload')) { sessionStorage.setItem('mkCartDateReload', '1'); location.reload(); }
      }
      return true;
    } catch (e) { return false; } finally { busy = false; }
  }
  const queue = () => { clearTimeout(timer); timer = setTimeout(() => sync({ reloadCartPage: true }).then(() => { check(); return welcome(); }), 1500); };

  window.fetch = function (input, init) {
    const p = nativeFetch(input, init);
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (CART_RE.test(url)) p.then(queue, queue);
    return p;
  };
  const xo = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (m, url) {
    if (CART_RE.test(String(url || ''))) this.addEventListener('loadend', queue);
    if (/identixweb\.com\/service\/api\/addons/.test(String(url || ''))) this.addEventListener('load', function () { learn(this.responseText); });
    return xo.apply(this, arguments);
  };

  /* Product page: if the cart already has a date, prefill the Identixweb picker with it. */
  function toUS(d) {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(d || ''); if (!m) return d;
    const dt = new Date(+m[3], +m[2] - 1, +m[1]);
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  async function prefill() {
    const w = document.querySelector('.identixweb-product-order-delivery, form[action*="/cart/add"] .identixweb-order-delivery');
    if (!w) return;
    const src = cartDate(await getCart()); if (!src) return;
    /* Oct 26: never prefill a date this product can't take (blocked, wrong weekday, too early/late). */
    const pidEl = document.querySelector('form[action*="/cart/add"] input[name="product-id"]');
    const pid = String((window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product && ShopifyAnalytics.meta.product.id) || (pidEl && pidEl.value) || '');
    await new Promise((r) => setTimeout(r, 1500)); // give the app's addons response a moment to land in rules
    if (invalidReason(src[1], pid)) return;
    let tries = 0;
    const t = setInterval(() => {
      const v = w.querySelector('.deliveryDateValue, input[name="delivery_date"]');
      if (!v) { if (++tries > 40) clearInterval(t); return; }
      clearInterval(t);
      if (v.value) return;
      v.value = src[1];
      const day = w.querySelector('.deliveryDayValue, input[name="delivery_day"]'); if (day) day.value = src[2] || '';
      const vis = w.querySelector('input.uiDeliveryCalendar'); if (vis) vis.value = src[1];
      w.classList.add('mk-date-from-cart');
      if (!w.querySelector('.mk-cart-date-note')) {
        const n = document.createElement('p'); n.className = 'mk-cart-date-note';
        n.textContent = 'Delivery date: ' + toUS(src[1]) + ' (same as the rest of your order)';
        w.prepend(n);
      }
    }, 250);
  }


  /* MISHKA Oct 21: delivery date is OPTIONAL. Identixweb's .post-date-error must never block Checkout (cart page or drawer):
     strip those nodes as they appear, and clear them in the capture phase before the app's checkout click check runs. */
  const CHK = 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .cart__checkout-button, [data-checkout]';
  /* Oct 26 datecheck: only strip it while the cart has NO invalid date (undated or valid date = Checkout allowed). */
  let bad = [], checked = false, ckBusy = false;
  const strip = () => { if (checked && !bad.length) document.querySelectorAll('.post-date-error').forEach((n) => n.remove()); };
  const MSG = "This delivery date isn't available. Please choose another date.";
  function paint() {
    const btns = document.querySelectorAll(CHK);
    btns.forEach((b) => {
      /* Oct 30 (Olia OK 8:41 AM PT): while the blocked-date notice is shown, Checkout is disabled + aria-disabled.
         Restored by 'Continue without a date' or a new valid date. Undated carts never reach here (bad is empty).
         data-mk-dd marks our disable; the $65 rule (data-mk-min) keeps its own flag so neither re-enables the other's block. */
      if (bad.length) { b.classList.add('mk-dd-blocked'); if (b.tagName !== 'A') b.disabled = true; b.setAttribute('aria-disabled', 'true'); b.dataset.mkDd = '1'; }
      else if (b.classList.contains('mk-dd-blocked') || b.dataset.mkDd) { b.classList.remove('mk-dd-blocked'); delete b.dataset.mkDd;
        if (!b.dataset.mkMin && !document.body.classList.contains('mk-min-blocked')) { b.removeAttribute('aria-disabled'); if ('disabled' in b && b.tagName !== 'A') b.disabled = false; } }
    });
    document.querySelectorAll('.mk-dd-invalid').forEach((n) => { if (!bad.length) n.remove(); });
    if (!bad.length) return;
    btns.forEach((btn) => {
      const h = btn.parentElement;
      if (!h || !btn.offsetParent && btn.id !== 'CartDrawer-Checkout' || h.querySelector(':scope > .mk-dd-invalid')) return;
      const box = document.createElement('div'); box.className = 'mk-dd-invalid'; box.setAttribute('role', 'alert');
      const names = bad.map((b) => b.item.product_title + ' (' + toUS(b.date) + ')').join(', ');
      box.innerHTML = '<p class="mk-dd-invalid__msg"></p><p class="mk-dd-invalid__items"></p>' +
        '<div class="mk-dd-invalid__actions"><a class="mk-dd-invalid__change" href="#">Choose new date</a>' +
        '<button type="button" class="mk-dd-invalid__clear">Continue without a date</button></div>';
      box.querySelector('.mk-dd-invalid__msg').textContent = MSG;
      box.querySelector('.mk-dd-invalid__items').textContent = names;
      const cu = bad[0].item.url || (root + 'collections/all'); box.querySelector('.mk-dd-invalid__change').href = cu + (cu.indexOf('?') > -1 ? '&' : '?') + 'mk_change=1';
      box.querySelector('.mk-dd-invalid__clear').addEventListener('click', clearBad);
      h.insertBefore(box, btn); // right above the (disabled) Checkout button, also inside the mobile sticky bar
    });
  }
  async function check(cart) {
    if (ckBusy && !cart) return; ckBusy = true;
    try { cart = cart || await getCart(); bad = badLines(cart); checked = true; paint(); strip(); }
    catch (e) {} finally { ckBusy = false; }
    return bad;
  }
  async function clearBad() {
    const set = {}; bad.forEach((b) => { set[b.key] = ''; });
    const a = (await getCart()).attributes || {};
    if (bad.some((b) => b.key === a._mk_date_src) || bad.some((b) => a._mk_date && String(a._mk_date).split(SEP)[0] === b.date)) { set._mk_date = ''; set._mk_date_src = ''; set._mk_date_copies = ''; set._mk_date_iso = ''; set._mk_date_label = ''; }
    await nativeFetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Mk-Cart-Date': '1' }, body: JSON.stringify({ attributes: set }) });
    bad = []; paint(); // Oct 29: enable Checkout right away
    if (window.MkLineDate && MkLineDate.sync) { try { await MkLineDate.sync(); } catch (e) {} }
    /* drop the 'Delivery' line property (Shopify ignores a deleted key, so blank it) */
    try { const c2 = await getCart(); if (!c2.attributes || !c2.attributes._mk_date) for (let i = 0; i < c2.items.length; i++) { const pr = c2.items[i].properties || {};
      if (pr.Delivery) await nativeFetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Mk-Cart-Date': '1', 'X-Mk-Line': '1' }, body: JSON.stringify({ line: i + 1, quantity: c2.items[i].quantity, properties: Object.assign({}, pr, { Delivery: '' }) }) }); } } catch (e) {}
    await check();
    if (document.getElementById('main-cart')) location.reload();
  }
  const css = document.createElement('style');
  css.textContent = '.mk-dd-invalid{background:#f9ece9;border:1px solid #422B47;border-radius:10px;padding:10px 12px;margin:0 0 10px;color:#422B47;font-family:"Trio Grotesk",inherit;font-size:14px;line-height:1.4;text-align:left;width:100%;box-sizing:border-box}' +
    '.mk-dd-invalid__msg{margin:0 0 4px;font-weight:600;font-family:Canela,Georgia,serif;font-size:16px}.mk-dd-invalid__items{margin:0 0 4px;opacity:.85;font-size:13px}' +
    '.mk-dd-invalid__actions{display:flex;gap:16px;flex-wrap:wrap;align-items:center}.mk-dd-invalid__actions a,.mk-dd-invalid__actions button{color:#422B47;text-decoration:underline;background:none;border:0;padding:0;min-height:44px;font:inherit;font-size:14px;cursor:pointer}' +
    '.mk-dd-invalid--flash{outline:3px solid #422B47;outline-offset:2px}';
  (document.head || document.documentElement).appendChild(css);
  const guard = (e) => {
    const t = e.type === 'submit' ? (e.submitter && e.submitter.matches && e.submitter.matches(CHK) ? e.submitter : null) : (e.target.closest && e.target.closest(CHK));
    if (!t || !bad.length) return false;
    e.preventDefault(); e.stopImmediatePropagation(); paint();
    /* Oct 29: the click is answered - the notice next to this button flashes and gets focus (never a silent dead button) */
    const m = (t.parentElement && t.parentElement.querySelector(':scope > .mk-dd-invalid')) || document.querySelector('.mk-dd-invalid');
    if (m) { if (m.scrollIntoView) m.scrollIntoView({ block: 'center', behavior: 'smooth' }); m.classList.remove('mk-dd-invalid--flash'); void m.offsetWidth; m.classList.add('mk-dd-invalid--flash'); const c = m.querySelector('.mk-dd-invalid__clear'); if (c) c.focus({ preventScroll: true }); setTimeout(() => m.classList.remove('mk-dd-invalid--flash'), 1600); }
    return true;
  };
  window.addEventListener('click', (e) => { if (guard(e)) return; if (e.target.closest && e.target.closest(CHK)) strip(); }, true);
  window.addEventListener('submit', (e) => { if (guard(e)) return; strip(); }, true);
  let pt; new MutationObserver(() => { clearTimeout(pt); pt = setTimeout(() => { paint(); strip(); }, 60); })
    .observe(document.documentElement, { childList: true, subtree: true });

  /* MISHKA welcomefix v2 (Oct 21): the code also lives in the discount_code cookie, which checkout re-applies.
     At Checkout (cart page or drawer) run welcome() first, but never longer than 2s and never block Checkout. */
  const withTimeout = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]);
  /* Oct 29 cart redo: show "One moment..." at once; welcome recheck runs in the background (non-blocking);
     only the line-date sync (Delivery property, max 2.5s) is awaited so the date reaches checkout. */
  const loading = (el) => { if (!el || el.dataset.mkLoading) return; const r = el.getBoundingClientRect(); el.style.width = r.width + 'px'; el.style.height = r.height + 'px';
    el.dataset.mkLoading = '1'; el.setAttribute('aria-busy', 'true'); if ('value' in el && el.tagName === 'INPUT') el.value = 'One moment...'; else el.textContent = 'One moment...'; };
  const preCheckout = (el) => { loading(el); try { welcome(null, { force: true, noReload: true }).catch(() => {}); } catch (e) {}
    return withTimeout(window.MkLineDate ? window.MkLineDate.sync().catch(() => {}) : Promise.resolve(), 2500).catch(() => {}); };
  let passing = false;
  window.addEventListener('submit', (e) => {
    if (passing || e.defaultPrevented) return;
    const f = e.target, sub = e.submitter;
    const act = (f && f.getAttribute && f.getAttribute('action')) || '';
    const isCheckout = (sub && (sub.name === 'checkout' || sub.matches && sub.matches(CHK))) || /\/checkout/.test(act);
    if (!isCheckout || !/\/(cart|checkout)/.test(act || '/cart')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const go = () => {
      passing = true;
      try {
        if (sub && sub.name) { const h = document.createElement('input'); h.type = 'hidden'; h.name = sub.name; h.value = sub.value || ''; f.appendChild(h); }
        if (window.MkLineDate && window.MkLineDate.changed) { location.href = root + 'checkout'; return; } /* lines were re-keyed: skip stale updates[] */
        HTMLFormElement.prototype.submit.call(f);
      } catch (err) { location.href = root + 'checkout'; }
    };
    preCheckout(sub && sub.matches && sub.matches(CHK) ? sub : f.querySelector('[name="checkout"]')).then(go, go);
  }, true);
  window.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href*="/checkout"]');
    if (!a || passing || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    const go = () => { passing = true; location.href = a.href; };
    preCheckout(a).then(go, go);
  }, true);

  /* Oct 30 eve two-dates fix: Identixweb writes product_<vid> on a calendar pick with a request our fetch/XHR hooks never
     see, so _mk_date*, and the Delivery property stayed on the old day until /cart ran sync(). After a pick on a product
     page whose variant is already a dated cart line, run the same sync() (resolve() takes the newest pick; Oct 29 re-key
     untouched) and then the line-date sync. Nothing runs for a variant not yet in the cart, so no date is ever removed. */
  const onPick = (e) => {
    const t = e.target; if (!(t instanceof Element) || !t.closest('.rmdp-day, .identixweb-order-delivery, #mk-dd-fb-s')) return;
    const f = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const vid = new URLSearchParams(location.search).get('variant') || (f && f.value) || '';
    [1500, 4000].forEach((ms) => setTimeout(async () => { try {
      const c = await getCart(), k = 'product_' + vid;
      if (!vid || !c.items.some((i) => 'product_' + i.variant_id === k) || !parts((c.attributes || {})[k])) return;
      if (await sync()) { if (window.MkLineDate && MkLineDate.sync) await MkLineDate.sync(); check(); }
    } catch (err) {} }, ms));
  };
  if (/\/products\//.test(location.pathname)) { document.addEventListener('click', onPick, true); document.addEventListener('change', onPick, true); }

  window.MkCartDate = { sync, welcome, resolve, check, invalidReason };
  const start = () => { check(); sync({ reloadCartPage: true }).then((changed) => { check(); if (!changed) sessionStorage.removeItem('mkCartDateReload'); return welcome(); }); prefill(); };
  window.addEventListener('pageshow', (e) => { if (e.persisted) welcome(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
